import { SafePromise } from '@benzinga/safe-await';
import { ExtendedSubscribable, Subscription } from '@benzinga/subscribable';

import { StockSymbol } from '@benzinga/session';
import { UUID } from '@benzinga/session';
import { Note } from './note';
import { NotesStore } from './store';
import { NotesRequest, NotesRestfulEvent } from './request';
import { WatchlistId } from '@benzinga/watchlist-manager';
import { Session } from '@benzinga/session';

export interface NotesUpdateEvent {
  notes: Note[];
  type: 'notes_updated';
}

export type NotesManagerEvent = NotesRestfulEvent | NotesUpdateEvent;

interface NotesFunctions {
  createNote: NotesManager['createNote'];
  deleteNote: NotesManager['deleteNote'];
  getNotes: NotesManager['getNotes'];
  getNotesFiltered: NotesManager['getNotesFiltered'];
  updateNote: NotesManager['updateNote'];
}

export class NotesManager extends ExtendedSubscribable<NotesManagerEvent, NotesFunctions> {
  private lastUpdated: Date | null;
  private store: NotesStore;
  private request: NotesRequest;
  private requestSubscription?: Subscription<NotesRequest>;

  constructor(session: Session) {
    super();
    this.lastUpdated = null;
    this.store = new NotesStore(this);
    this.request = new NotesRequest(session, this.store);
  }

  public static getName = () => 'benzinga-notes';

  public createNote = async (symbol: StockSymbol, text: string, watchlistId?: WatchlistId): SafePromise<Note> => {
    const newNote = await this.request.createNote(symbol, text, watchlistId);
    if (newNote.err) {
      return { err: newNote.err };
    } else {
      if (newNote.ok.state === 'new') {
        this.dispatch({
          notes: this.store.getNotes(),
          type: 'notes_updated',
        });
      }
      return { ok: newNote.ok.note };
    }
  };

  public getNotes = async (): SafePromise<Note[]> => {
    const numberOfMillisecondsIn1Mins = 60000;
    if (Date.now() - (this.lastUpdated?.getTime() ?? 0) > numberOfMillisecondsIn1Mins) {
      const notes = await this.request.getNotes();
      if (notes.ok) {
        this.lastUpdated = new Date();
        if (notes.ok.state === 'updated') {
          this.dispatch({
            notes: notes.ok.notes,
            type: 'notes_updated',
          });
        }
        return { ok: notes.ok.notes };
      }
    }
    return { ok: this.store.getNotes() };
  };

  public getNotesFiltered = async (symbol: StockSymbol): SafePromise<Note[]> => {
    const notes = await this.request.getNotesFiltered(symbol);
    if (notes.err) {
      return { err: notes.err };
    } else {
      this.lastUpdated = new Date();
      if (notes.ok.state === 'updated') {
        this.dispatch({
          notes: this.store.getNotes(),
          type: 'notes_updated',
        });
      }
      return { ok: notes.ok.notes };
    }
  };

  public getNotesFilteredStored = (symbol: StockSymbol): Note[] => {
    return this.store.getNotes().filter(note => note.getSymbol() === symbol);
  };

  public getNote = async (uuid: UUID): SafePromise<Note> => {
    const notes = await this.request.getNotesUUID(uuid);
    if (notes.err) {
      return { err: notes.err };
    } else {
      this.lastUpdated = new Date();
      if (notes.ok.state === 'updated') {
        this.dispatch({
          notes: this.store.getNotes(),
          type: 'notes_updated',
        });
      }
      return { ok: notes.ok.note };
    }
  };

  public updateNote = async (note: Note, text: string): SafePromise<Note> => {
    const updatedNote = await this.request.updateNote(note, text);
    if (updatedNote.err) {
      return { err: updatedNote.err };
    } else {
      if (updatedNote.ok.state !== 'cache') {
        this.dispatch({
          notes: this.store.getNotes(),
          type: 'notes_updated',
        });
      }
      return { ok: updatedNote.ok.note };
    }
  };

  public deleteNote = async (note: Note): SafePromise<undefined> => {
    const val = await this.request.deleteNote(note);
    if (val.err === undefined) {
      this.store.deleteNote(note);
      this.dispatch({
        notes: this.store.getNotes(),
        type: 'notes_updated',
      });
    }
    return val;
  };

  protected onSubscribe(): NotesFunctions {
    return {
      createNote: this.createNote,
      deleteNote: this.deleteNote,
      getNotes: this.getNotes,
      getNotesFiltered: this.getNotesFiltered,
      updateNote: this.updateNote,
    };
  }

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
  }
}
